// mdfs.js orchestration (plan §7.5).
//
// Contract with the host page: this code NEVER throws into it. Every entry
// point (start, scan, per-test callbacks) is wrapped, and every failure
// mode — bad markup, blocked storage, no fetch, timeout, HTTP error, malformed
// JSON, no-fill — ends in the test's fallback, not an exception.
import { log, resolveConfig } from './config';
import { attribution, isOpaqueToken, sessionId } from './storage';
import { beacon, decide } from './api';
import { renderOffers } from './render';
import type { Config, DecisionResponse } from './types';

export const VERSION = '1';

/** Per-element lifecycle marker: pending → filled | fallback | empty | error. */
export const STATE_ATTR = 'data-mdfs-state';
const SELECTOR = '[data-mdfs-test]';

// Every `res.*` / `fb.*` / `offer.*` read below is an own-property read, and
// none of them says so: `decide()` hands back an own-properties-only copy of
// the body (`ownProperties` in ./api), so a host page's polluted
// `Object.prototype` cannot reach any of them. #48 guarded `fallback.remove`
// here with an explicit `hasOwnProperty`; #75 replaced that with the boundary
// copy, because a per-property guard only covers the properties somebody
// remembered.

function ready(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    // The tag is `async`: by the time it executes the DOM is often already
    // parsed and DOMContentLoaded will never fire again.
    fn();
  }
}

function fallback(el: Element, res: DecisionResponse | null, cfg: Config): void {
  const fb = (res && res.fallback) || null;
  const mode = (fb && typeof fb.mode === 'string' && fb.mode) || 'collapse';
  log('fallback', mode, res && res.reason);

  if (mode === 'default_offer' && fb && fb.offers && fb.offers.length) {
    if (renderOffers(el, fb.offers, cfg)) {
      el.setAttribute(STATE_ATTR, 'fallback');
      // `res!`: a truthy `fb` came off it. What makes this a beacon-worthy
      // response — an id *and* the tag proving where it came from (#94) — is
      // checked in `beacon` itself.
      beacon(cfg, res!);
      return;
    }
  }
  // `collapse`: leave the reserved container in place and empty — removing it
  // would shift the page. Removal happens only when *this response* asks for
  // it, which is an own-property read by construction (#48, #75): a host page
  // carrying a polluted `Object.prototype.remove` cannot make an unrelated
  // `fallback: {}` tear the container out of its own layout.
  if (fb && fb.remove === true) {
    el.setAttribute(STATE_ATTR, 'removed');
    if (el.parentNode) el.parentNode.removeChild(el);
    return;
  }
  el.setAttribute(STATE_ATTR, 'empty');
}

function paint(el: Element, res: DecisionResponse | null, cfg: Config): void {
  try {
    const offers = res && res.fill === true && res.offers && res.offers.length ? res.offers : null;
    if (offers && renderOffers(el, offers, cfg)) {
      el.setAttribute(STATE_ATTR, 'filled');
      beacon(cfg, res!);
      return;
    }
    fallback(el, res, cfg);
  } catch (err) {
    log('render err', err);
    try {
      el.setAttribute(STATE_ATTR, 'error');
    } catch {
      /* detached node */
    }
  }
}

function serve(el: Element, cfg: Config, sid: string, attr: Record<string, string>): void {
  const site = el.getAttribute('data-mdfs-site') || '';
  const test = el.getAttribute('data-mdfs-test') || '';
  // No API base at all (`null`, as opposed to `''` for a deliberately
  // same-origin embed) means the tag was pasted without `data-mdfs-api`. That
  // used to POST /v1/decision to the customer's own origin — a confusing 404 in
  // their logs instead of an obvious misconfiguration in ours (#51). Fail
  // loudly and locally instead: no request leaves the page, the test is
  // marked `error` in the DOM, and debug mode names the reason.
  const why = cfg.api == null ? 'no api base' : !site || !test ? 'bad test' : '';
  if (why) {
    log(why, el);
    el.setAttribute(STATE_ATTR, 'error');
    return;
  }
  const override = parseInt(el.getAttribute('data-mdfs-timeout') || '', 10);
  const timeout = override > 0 ? override : cfg.timeout;

  // Payload order and key names per plan §7.1. Attribution params are sent
  // under their configured names and omitted entirely when not captured.
  const body: Record<string, unknown> = { site, test, session_id: sid };
  // `attr` has no prototype (`attribution` builds it with `Object.create(null)`
  // — #109), so this enumerates the captured params and nothing else. It used
  // to walk `Object.prototype`, and because the loop runs *after* the three
  // assignments above, a host page's inherited `site` / `test` /
  // `session_id` overwrote them and re-routed the request; the service answers
  // 200 with a DEFAULT fallback either way, so the page just quietly stopped
  // filling. The guarantee lives at the boundary that builds the map, not in a
  // per-key check here — the same trade `ownProperties` makes inbound — and
  // test/prototype.test.ts asserts the null prototype directly so a later `{}`
  // in storage.ts cannot re-open this silently.
  for (const k in attr) body[k] = attr[k];
  body.page = location.pathname;
  // Charset-checked like the attribution params (#167): this one comes straight
  // out of publisher-authored markup, so a template that interpolates a visitor's
  // email into it would otherwise put the address in the payload, in the click
  // token we mint, and in the advertiser's query string. Dropped to null, never
  // trimmed — same trade the service makes, and it keeps the request useful.
  const experiment = el.getAttribute('data-mdfs-experiment');
  body.experiment = experiment && isOpaqueToken(experiment) ? experiment : null;

  log('req', timeout, body);
  decide(cfg, body, timeout).then(
    (res) => paint(el, res, cfg),
    (err) => {
      log('req err', err);
      paint(el, null, cfg);
    },
  );
}

/**
 * Discover and serve every unclaimed test in the document.
 *
 * Idempotent: elements are claimed with `data-mdfs-state` before the request
 * goes out, so a second scan (a second copy of the script, or an explicit
 * `mdfs.refresh()` after injecting markup) never double-renders.
 */
export function scan(cfg: Config): void {
  try {
    const nodes = document.querySelectorAll(SELECTOR);
    if (!nodes.length) {
      log('no tests');
      return;
    }
    const sid = sessionId();
    const attr = attribution(cfg.params, location.search);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (el.getAttribute(STATE_ATTR)) continue;
      el.setAttribute(STATE_ATTR, 'pending');
      try {
        serve(el, cfg, sid, attr);
      } catch (err) {
        log('serve err', err);
        el.setAttribute(STATE_ATTR, 'error');
      }
    }
  } catch (err) {
    log('scan err', err);
  }
}

interface MdfsApi {
  version: string;
  refresh: () => void;
  config: Config;
}

/**
 * Entry point. Safe to run twice (two copies of the tag on one page): the
 * second run sees `window.mdfs` and returns without scheduling anything.
 */
export function start(script?: Element | null): void {
  try {
    const w = window as unknown as { mdfs?: MdfsApi; MDFS_CONFIG?: unknown };
    if (w.mdfs && w.mdfs.version) {
      log('dup include');
      return;
    }
    const tag = script === undefined ? document.currentScript : script;
    const cfg = resolveConfig(tag, w.MDFS_CONFIG);
    w.mdfs = {
      version: VERSION,
      refresh: function () {
        scan(cfg);
      },
      config: cfg,
    };
    ready(function () {
      scan(cfg);
    });
  } catch {
    /* the page must never see an exception from us */
  }
}
